/**
 * Die Detailansicht eines Einsatzes — `PWA-002`, nach `07` §7.8.
 *
 * **„Die Detailansicht zeigt zuerst die nächste sinnvolle Handlung, nicht alle
 * Funktionen gleichzeitig."** Der Satz steht dort wörtlich und ist der ganze
 * Bauplan dieser Datei. Deshalb steht oben genau ein großer Knopf — der
 * Übergang, der als Nächstes dran ist —, und alles andere folgt darunter in
 * der Reihenfolge, in der es gebraucht wird.
 *
 * Welche Übergänge möglich sind, entscheidet **nicht diese Datei**, sondern
 * die Tabelle in `packages/sync`. Das ist der Unterschied zwischen einer
 * Oberfläche, die die Regeln kennt, und einer, die sie nachbaut: Die zweite
 * driftet, sobald sich ADR-045 ändert, und niemand merkt es, weil die Knöpfe
 * ja aussehen wie immer.
 *
 * **Was der Monteur nicht darf, steht auch nicht da.** Nach ADR-045 schaltet
 * er `geplant → anfahrt → vor-ort ↔ wartet → abschluss → uebermittelt`, und
 * dort endet sein Weg: `uebermittelt → geprueft` und jede Stornierung liegen
 * beim Büro. Ein ausgegrauter Knopf „Prüfen" wäre eine Einladung zu einem
 * Anruf, der nichts bringt.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";

import { findeAuftrag, findeKunde } from "@hwc/demo-data";
import type { Einsatz } from "@hwc/demo-data";
import type { Rolle } from "@hwc/contracts";
import type { EinsatzZustand } from "@hwc/sync";
import { UEBERGAENGE } from "@hwc/sync";
import { darfSchalten } from "@hwc/auth";

import { useMonteur } from "../../src/Sitzung.js";
import {
  Einsatzabzeichen,
  Grossknopf,
  Knopfleiste,
  Leer,
  Ortszeile,
  Wartemarke,
  datum,
  zeitfenster,
  zustandText,
} from "../../src/bausteine.js";
import { Zeiterfassung } from "../time/Zeiterfassung.js";
import { Materialerfassung } from "../time/Materialerfassung.js";
import { Fotos } from "../media/Fotos.js";
import { Ressourcen } from "../resources/Ressourcen.js";
import { Abschlussansicht } from "../report/Abschlussansicht.js";
import { Buerochat } from "../resources/Buerochat.js";

/**
 * Was ein Übergang für den Monteur heißt — in seinen Worten, nicht in denen
 * des Automaten.
 *
 * „Losfahren" statt „Zustand auf anfahrt setzen". Der Zustandsname steht
 * daneben im Abzeichen; der Knopf beschreibt die Handlung.
 */
const HANDLUNG: Partial<Record<EinsatzZustand, string>> = {
  anfahrt: "Losfahren",
  "vor-ort": "Angekommen",
  wartet: "Unterbrechen",
  abschluss: "Arbeit fertig",
  uebermittelt: "Bericht senden",
};

/**
 * Die Übergänge, die dieser Monteur an diesem Einsatz gehen darf.
 *
 * **Über `darfSchalten` aus `packages/auth`, nicht über eine eigene Liste.**
 * Hier stand bis zum 05.09.2026 ein `NUR_BUERO = ["geprueft", "storniert"]` —
 * und es war unvollständig: `uebermittelt → abschluss` gehört nach ADR-045
 * ebenfalls dem Büro, fehlte aber. Die App bot dem Monteur damit an, seinen
 * eigenen übermittelten Bericht zurückzuholen.
 *
 * Genau davor warnt der Kommentar am Kopf dieser Datei: Eine Oberfläche, die
 * die Regeln nachbaut, driftet — und niemand merkt es, weil die Knöpfe
 * aussehen wie immer. Aufgefallen ist es erst, als das Büro tatsächlich
 * zurückgeben konnte und beide Seiten denselben Knopf zeigten.
 */
function erlaubteUebergaenge(
  einsatz: Einsatz,
  rollen: readonly Rolle[],
  istZugewiesen: boolean,
): readonly EinsatzZustand[] {
  return UEBERGAENGE[einsatz.zustand].filter(
    (nach) =>
      darfSchalten({ von: einsatz.zustand, nach, rollen, istZugewiesen }) !== "nein",
  );
}


/**
 * Das Feld für die begründete Ausnahme — `07` §7.8.
 *
 * **Verzögert gespeichert wie die Tätigkeitsnotiz**, aus demselben Grund: Auf
 * einem Handy löst `blur` erst aus, wenn irgendwo anders hingetippt wird, und
 * wer mitten im Satz das Gerät weglegt, hätte nichts gespeichert.
 *
 * Der Platzhalter nennt ein Beispiel statt einer Aufforderung. „Begründung
 * eingeben" sagt nicht, was eine taugliche Begründung ist — und das Büro liest
 * sie später beim Prüfen.
 */
function Ausnahmefeld({
  wert,
  beiAenderung,
}: {
  wert: string;
  beiAenderung: (text: string) => void;
}) {
  const [text, setzeText] = useState(wert);
  const letzte = useRef(wert);

  useEffect(() => {
    if (text === letzte.current) return;
    const marke = setTimeout(() => {
      letzte.current = text;
      beiAenderung(text);
    }, 1000);
    return () => clearTimeout(marke);
  }, [text, beiAenderung]);

  return (
    <label className="checkliste__grund">
      <span className="checkliste__grundname">
        Bleibt offen, weil …
      </span>
      <textarea
        className="notizfeld"
        rows={2}
        value={text}
        placeholder="z. B. Messgerät defekt, Nachholung am Folgetag vereinbart"
        onChange={(e) => setzeText(e.target.value)}
        onBlur={() => {
          if (text === letzte.current) return;
          letzte.current = text;
          beiAenderung(text);
        }}
      />
    </label>
  );
}

export function Einsatzdetail() {
  const { kennung } = useParams();
  const {
    bestand, monteur, zustandVon, auflage, wartet, schalte, hakeSchritt, setzeNotiz,
    setzeAusnahme,
  } = useMonteur();

  const einsatz = bestand.einsaetze.find((e) => e.ref.opaqueId === kennung);

  if (!einsatz) {
    return (
      <>
        <h1>Einsatz nicht gefunden</h1>
        <p className="leise">
          <Link to="/">Zurück zu heute</Link>
        </p>
      </>
    );
  }

  const auftrag = findeAuftrag(bestand, einsatz.auftrag);
  const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;
  const ort = auftrag?.einsatzort ?? kunde?.anschrift;
  const zustand = zustandVon(einsatz);
  const eigene = auflage(einsatz);

  const zugewiesen =
    einsatz.monteur !== undefined
    && einsatz.monteur.opaqueId === monteur.ref.opaqueId;

  // Der Zustand kommt aus `zustandVon` — mit lokaler Auflage und geteiltem
  // Stand —, die Rechte aus der Tabelle. Beides zusammen ergibt, was hier
  // angeboten wird.
  const moeglich = erlaubteUebergaenge(
    { ...einsatz, zustand },
    monteur.rollen,
    zugewiesen,
  );
  // Der erste mögliche Übergang ist der Weg nach vorn — die Tabelle in
  // `packages/sync` listet ihn zuerst. „Unterbrechen" steht bewusst nicht
  // oben: Es ist ein Nebenweg und soll nicht so aussehen wie der Hauptweg.
  const naechster = moeglich.find((z) => z !== "wartet");
  const nebenwege = moeglich.filter((z) => z !== naechster);

  const schritte = einsatz.schritte.map((s) => {
    const lokal = eigene.ausnahmen?.[s.ref.opaqueId];
    return {
      ...s,
      erledigt: s.erledigt || eigene.erledigteSchritte.includes(s.ref.opaqueId),
      // Die lokale Begründung schlägt die aus dem Bestand: Sie ist die
      // jüngere, und sie stammt von dem, der vor Ort steht.
      ...(lokal ? { ausnahme: lokal } : {}),
    };
  });

  /**
   * Was den Abschluss blockiert: offene Pflichtschritte **ohne** Begründung.
   *
   * `07` §7.8 nennt als Bedingung „Checkliste vollständig **oder begründete
   * Ausnahme**". Bis zum 05.09.2026 kannte die App nur die erste Hälfte: Der
   * Knopf war gesperrt, sobald ein Pflichtschritt offen war, und der Hinweis
   * daneben versprach eine Ausnahme, zu der kein Weg führte.
   *
   * Das ist nicht bloß unbequem. Ein Monteur, der seinen fertigen Bericht
   * nicht abschicken kann, hakt den Schritt ab, den er nicht ausführen konnte
   * — die Sperre erzeugt genau die falsche Angabe, die sie verhindern soll.
   * Mit Begründung geht es weiter, und der Grund steht danach in der internen
   * Notiz des Berichts, wo das Büro ihn beim Prüfen liest (`07` §7.16).
   */
  const offenePflicht = schritte.filter((s) => s.pflicht && !s.erledigt);
  const blockierend = offenePflicht.filter((s) => !s.ausnahme?.trim());

  return (
    <>
      <p className="rueckweg">
        <Link to="/">← Heute</Link>
      </p>

      <header className="detailkopf">
        <div className="detailkopf__zeile">
          <Einsatzabzeichen zustand={zustand} />
          <Wartemarke anzahl={wartet(einsatz)} />
        </div>
        <h1>{einsatz.titel}</h1>
        <p className="leise">
          {datum(einsatz.von)} · {zeitfenster(einsatz.von, einsatz.bis)}
        </p>
        <p>
          {kunde?.name}
          {auftrag && <> · Auftrag {auftrag.nummer}</>}
        </p>
        {ort && <Ortszeile anschrift={ort} />}
      </header>

      {!zugewiesen && (
        <p className="marke marke--warten">
          Dieser Einsatz ist nicht Ihnen zugewiesen. Erfassen können Sie ihn — beim
          Senden lehnt die Zustellung ihn ab, und lokal Erfasstes wird nach
          `04` §4.12 entfernt.
        </p>
      )}

      {einsatz.hinweis && <p className="hinweiskasten">{einsatz.hinweis}</p>}
      {einsatz.wartetAuf && (
        <p className="marke marke--warten">Wartet: {einsatz.wartetAuf}</p>
      )}

      {/*
        Die nächste Handlung. Genau eine, groß, oben — `07` §7.8.
      */}
      {naechster ? (
        <Knopfleiste>
          <Grossknopf
            art="haupt"
            onClick={() => schalte(einsatz, naechster)}
            disabled={naechster === "uebermittelt" && blockierend.length > 0}
          >
            {HANDLUNG[naechster] ?? zustandText(naechster)}
          </Grossknopf>
          {nebenwege.map((z) => (
            <Grossknopf key={z} onClick={() => schalte(einsatz, z)}>
              {HANDLUNG[z] ?? zustandText(z)}
            </Grossknopf>
          ))}
        </Knopfleiste>
      ) : (
        <p className="hinweiskasten">
          {zustand === "uebermittelt"
            ? "Bericht ist beim Büro. Freigabe oder Rückgabe entscheidet das Büro — "
              + "das können Sie von hier aus nicht."
            : zustand === "geprueft"
              ? "Geprüft und abgeschlossen."
              : "Dieser Einsatz wurde storniert."}
        </p>
      )}

      {naechster === "uebermittelt" && blockierend.length > 0 && (
        <p className="marke marke--warten">
          Noch {blockierend.length} Pflichtschritt
          {blockierend.length === 1 ? "" : "e"} ohne Begründung. Erledigen oder in der
          Checkliste begründen, warum {blockierend.length === 1 ? "er" : "sie"} offen
          {blockierend.length === 1 ? " bleibt" : " bleiben"} — der Grund geht mit an
          das Büro.
        </p>
      )}

      {naechster === "uebermittelt"
        && blockierend.length === 0
        && offenePflicht.length > 0 && (
        <p className="marke">
          {offenePflicht.length} Pflichtschritt
          {offenePflicht.length === 1 ? " bleibt" : "e bleiben"} begründet offen. Die
          Begründung steht im Bericht — sichtbar für das Büro, nicht für den Kunden.
        </p>
      )}

      <h2>Checkliste</h2>
      {/*
        Jeder Schritt ist nach ADR-015 eine eigene Unteraufgabe mit eigenem
        ResourceRef. Deshalb erzeugt jedes Häkchen eine eigene Operation — und
        deshalb kollidieren zwei Monteure an verschiedenen Schritten nicht
        (`04` §4.12).
      */}
      <ul className="checkliste">
        {schritte.map((s) => (
          <li key={s.ref.opaqueId}>
            <label className="checkliste__punkt">
              <input
                type="checkbox"
                checked={s.erledigt}
                disabled={zustand === "geprueft" || zustand === "storniert"}
                onChange={(e) => hakeSchritt(einsatz, s.ref.opaqueId, e.target.checked)}
              />
              <span>
                {s.text}
                {s.pflicht && <span className="checkliste__pflicht">Pflicht</span>}
                {s.ausnahme && (
                  <span className="checkliste__ausnahme">Ausnahme: {s.ausnahme}</span>
                )}
              </span>
            </label>

            {/*
              Das Begründungsfeld erscheint nur, wo es gebraucht wird: an einem
              offenen Pflichtschritt. An jedem Schritt stünde es als Einladung,
              die Checkliste wegzubegründen — und das ist das Gegenteil dessen,
              was `07` §7.8 meint.
            */}
            {s.pflicht && !s.erledigt && zustand !== "geprueft"
              && zustand !== "storniert" && (
              <Ausnahmefeld
                wert={s.ausnahme ?? ""}
                beiAenderung={(text) => setzeAusnahme(einsatz, s.ref.opaqueId, text)}
              />
            )}
          </li>
        ))}
      </ul>

      <Zeiterfassung einsatz={einsatz} />
      <Materialerfassung einsatz={einsatz} />
      <Fotos einsatz={einsatz} />

      <h2>Tätigkeitsnotiz</h2>
      <Notizfeld
        wert={eigene.notiz ?? ""}
        beiAenderung={(text) => setzeNotiz(einsatz, text)}
        gesperrt={zustand === "geprueft" || zustand === "storniert"}
      />

      <Buerochat einsatz={einsatz} />

      <Abschlussansicht einsatz={einsatz} />

      <Ressourcen einsatz={einsatz} />
    </>
  );
}

/**
 * Das Notizfeld.
 *
 * **Es speichert nach einer Sekunde Ruhe — und zusätzlich beim Verlassen.**
 *
 * Der erste Entwurf speicherte *nur* beim Verlassen, mit einem richtigen
 * Argument: Ein Feld, das je Tastendruck eine Offlineoperation erzeugt, füllt
 * die Warteschlange mit hundert Versionen desselben Satzes, und jede wäre nach
 * `04` §4.12 ein möglicher Konflikt.
 *
 * Nur war die Folgerung falsch. Gemessen am 05.09.2026: Die Notiz kam nie in
 * der Auflage an, der erzeugte Bericht trug „Ohne Tätigkeitsnotiz", und die
 * Zusammenfassung meldete sie als fehlend — richtig, aber zu spät. Auf einem
 * Touchgerät ist „Fokusverlust" unzuverlässig: Wer die App wegwischt, das
 * Handy sperrt oder direkt auf „Arbeit fertig" tippt, hat den Text
 * geschrieben und nicht gespeichert. Und `07` §7.8 führt die Tätigkeitsnotiz
 * unter den Pflichtbestandteilen des Abschlusses.
 *
 * Eine Sekunde Ruhe löst beides: Es entsteht eine Operation je Denkpause statt
 * je Zeichen, und verloren geht nichts. Dass mehrere davon die
 * Warteschlange nicht füllen, sorgt `erfasse` — eine Freitextoperation
 * ersetzt die vorherige zum selben Vorgang.
 *
 * `07` §7.14 nennt Sprachnotizen als Alternative zu langen Texteingaben. Die
 * Aufnahme gehört zu `PWA-004` und braucht Gerätezugriff; der Hinweis steht
 * hier, damit der Platz dafür nicht vergessen wird.
 */
function Notizfeld({
  wert,
  beiAenderung,
  gesperrt,
}: {
  wert: string;
  beiAenderung: (text: string) => void;
  gesperrt: boolean;
}) {
  const [entwurf, setzeEntwurf] = useState(wert);

  // Der Rückgriff in einem Ref, nicht in der Abhängigkeitsliste: Sonst
  // startete der Zeitgeber bei jedem Render neu, und bei zügigem Tippen
  // liefe er nie ab.
  const speichern = useRef(beiAenderung);
  speichern.current = beiAenderung;

  useEffect(() => {
    if (entwurf === wert) return;
    const uhr = setTimeout(() => speichern.current(entwurf), 1000);
    return () => clearTimeout(uhr);
  }, [entwurf, wert]);

  return (
    <>
      <textarea
        className="notizfeld"
        rows={4}
        value={entwurf}
        disabled={gesperrt}
        placeholder="Was wurde gemacht? Kurz und in ganzen Sätzen — daraus entsteht der Servicebericht."
        onChange={(e) => setzeEntwurf(e.target.value)}
        onBlur={() => {
          // Zusätzlich, nicht stattdessen: Wer das Feld verlässt, wartet nicht
          // noch eine Sekunde auf seinen eigenen Text.
          if (entwurf !== wert) beiAenderung(entwurf);
        }}
      />
      <p className="leise">
        Wird kurz nach dem Tippen gespeichert, spätestens beim Verlassen des Feldes.
        Eine Sprachnotiz statt Tippen ist in `07` §7.14 vorgesehen und entsteht mit dem
        Gerätezugriff aus `PWA-004`.
      </p>
    </>
  );
}

/** Die Liste aller Einsätze — der zweite Bereich aus `07` §7.7. */
export function Einsatzliste() {
  const { bestand, monteur, heute, zustandVon } = useMonteur();
  const [frage, setzeFrage] = useState("");
  const [zeigeErledigte, setzeErledigte] = useState(false);

  const heuteTag = heute.toISOString().slice(0, 10);
  const q = frage.trim().toLowerCase();

  const meine = bestand.einsaetze
    .filter((e) => e.monteur?.opaqueId === monteur.ref.opaqueId)
    .filter((e) => {
      const z = zustandVon(e);
      // „Abgeschlossene Einsätze nur bei Bedarf" — `07` §7.7.
      return zeigeErledigte || (z !== "geprueft" && z !== "storniert");
    })
    .filter((e) => {
      if (!q) return true;
      const auftrag = findeAuftrag(bestand, e.auftrag);
      const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;
      const ort = auftrag?.einsatzort ?? kunde?.anschrift;
      return (
        e.titel.toLowerCase().includes(q)
        || (kunde?.name.toLowerCase().includes(q) ?? false)
        || (auftrag?.nummer.includes(q) ?? false)
        || (ort?.ort.toLowerCase().includes(q) ?? false)
        || (ort?.strasse.toLowerCase().includes(q) ?? false)
      );
    })
    .slice()
    .sort((a, b) => a.von.localeCompare(b.von));

  const gruppen: readonly (readonly [string, typeof meine])[] = [
    ["Heute", meine.filter((e) => e.von.slice(0, 10) === heuteTag)],
    ["Später", meine.filter((e) => e.von.slice(0, 10) > heuteTag)],
    ["Vorbei", meine.filter((e) => e.von.slice(0, 10) < heuteTag)],
  ];

  return (
    <>
      <h1>Einsätze</h1>

      {/* `07` §7.7: „Suche nach Kunde, Adresse oder Auftragsnummer." */}
      <input
        className="suchfeld"
        type="search"
        placeholder="Kunde, Adresse oder Auftragsnummer"
        aria-label="Einsätze durchsuchen"
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
      />

      <label className="schalter">
        <input
          type="checkbox"
          checked={zeigeErledigte}
          onChange={(e) => setzeErledigte(e.target.checked)}
        />
        Abgeschlossene mitzeigen
      </label>

      {meine.length === 0 ? (
        <Leer text={q ? `Nichts gefunden zu „${frage}".` : "Ihnen ist nichts zugewiesen."} />
      ) : (
        gruppen.map(([name, liste]) =>
          liste.length === 0 ? null : (
            <div key={name}>
              <h2>{name}</h2>
              {liste.map((e) => (
                <KurzeKarte key={e.ref.opaqueId} kennung={e.ref.opaqueId} />
              ))}
            </div>
          ),
        )
      )}
    </>
  );
}

function KurzeKarte({ kennung }: { kennung: string }) {
  const { bestand, zustandVon, wartet } = useMonteur();
  const einsatz = bestand.einsaetze.find((e) => e.ref.opaqueId === kennung);
  if (!einsatz) return null;

  const auftrag = findeAuftrag(bestand, einsatz.auftrag);
  const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;

  return (
    <Link className="kurzkarte" to={`/einsatz/${einsatz.ref.opaqueId}`}>
      <span className="kurzkarte__zeit">
        {datum(einsatz.von)}
        <br />
        {zeitfenster(einsatz.von, einsatz.bis)}
      </span>
      <span className="kurzkarte__haupt">
        <span className="kurzkarte__titel">{einsatz.titel}</span>
        <span className="leise">{kunde?.name}</span>
      </span>
      <span className="kurzkarte__marken">
        <Einsatzabzeichen zustand={zustandVon(einsatz)} />
        <Wartemarke anzahl={wartet(einsatz)} />
      </span>
    </Link>
  );
}
